/*
 * (c) 2023 Amazon Web Services, Inc. or its affiliates. All Rights Reserved.
 * 
 * This AWS Content is provided subject to the terms of the AWS Customer Agreement available at http://aws.amazon.com/agreement or other written agreement between Customer and either Amazon Web Services, Inc. or Amazon Web Services EMEA SARL or both.
 */

import '@aws-amplify/ui-react/styles.css';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signInWithRedirect, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { useEffect, useState } from 'react';
import axios from 'axios';

import './App.css';
import Application from "./components/Application";
import { Button, ContentLayout, Header, SpaceBetween } from "@cloudscape-design/components";
import { getAmplifyConfig } from './amplify-config';
import workbenchLogo from './assets/workbench_logo_white.png';


try {
  const amplifyConfig = getAmplifyConfig();
  Amplify.configure(amplifyConfig);
} catch (error) {
  console.error('Failed to configure Amplify:', error);
}


interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
  error: string | null;
}

export default function App() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null
  });

  // Check authentication status on mount
  useEffect(() => {
    checkAuthState();
  }, []);

  // Visibility change check - validate session when user returns to tab
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && authState.isAuthenticated) {
        try {
          await fetchAuthSession();
        } catch (error) {
          console.log('Session expired on visibility change, signing out...');
          await handleSignOut();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [authState.isAuthenticated]);

  const checkAuthState = async () => {
    console.log('🔍 Debug - Starting checkAuthState...');
    try {
      console.log('🔍 Debug - Calling getCurrentUser...');
      const user = await getCurrentUser();
      console.log('🔍 Debug - getCurrentUser success:', user);
      
      console.log('🔍 Debug - Getting email from ID token...');
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      const email = idToken?.payload?.email || user.username;
      
      console.log('🔍 Debug - ID token payload:', idToken?.payload);
      console.log('🔍 Debug - Email from token:', email);
      
      const userAttributes = { email };
      
      console.log('🔍 Debug - Email for bug reports:', userAttributes.email || user.username);
      
      console.log('🔍 Debug - Setting authenticated state...');
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: {
          ...user,
          attributes: userAttributes
        },
        error: null
      });
      console.log('🔍 Debug - Auth state set successfully');
    } catch (error) {
      console.error('🔍 Debug - checkAuthState failed:', error);
      console.error('🔍 Debug - Error details:', error instanceof Error ? error.message : 'Unknown error', error instanceof Error ? error.stack : 'No stack trace');
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null
      });
    }
  };

  // HTTP interceptor to handle Access Token expiration
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            console.log('[HTTP Interceptor] 401 detected, refreshing session...');
            const session = await fetchAuthSession({ forceRefresh: true });
            originalRequest.headers.Authorization = `Bearer ${session.tokens?.accessToken?.toString()}`;
            return axios.request(originalRequest);
          } catch (refreshError) {
            console.error('[HTTP Interceptor] Session refresh failed:', refreshError);
            await signOut();
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );
    
    return () => axios.interceptors.response.eject(responseInterceptor);
  }, []);

  const handleSignIn = async () => {
    try {
      await signInWithRedirect();
      await checkAuthState();
    } catch (error) {
      console.error('Sign in error:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      // Note: We don't clear localStorage here to preserve stream state
      // The Game component's stream reconnection logic will handle this
      await signOut();
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null
      });
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  if (authState.isLoading) {
    return (
      <ContentLayout
        disableOverlap
        headerVariant="high-contrast"
        header={
          <SpaceBetween size="xl">
            <div style={{backgroundColor: '#232F3E', padding: '20px'}} >
            <Header
              variant="h1"
              description={ <span style={{ fontSize: '18px', color:'white'}}>
              Loading...</span>}>
              <img 
                src={workbenchLogo} 
                alt="Workbench Streams"
                style={{
                  height: '48px',
                  width: 'auto',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  padding: '5px', // Optional: adds some space around the image
                  borderRadius: '4px' // Optional: rounds the corners
                }}
              />
            </Header>
            </div>             
          </SpaceBetween>
        }>
      </ContentLayout>
    );
  }

  if (authState.error) {
    return <div>Encountering error... {authState.error}</div>;
  }

  if (authState.isAuthenticated) {
    return (
      <Application signOut={handleSignOut} auth={authState}></Application>
    );
  }

  return (          
    <ContentLayout
      disableOverlap
      headerVariant="high-contrast"
      header={
        <SpaceBetween size="xl">
          <div style={{backgroundColor: '#232F3E', padding: '20px'}} >
          <Header
            variant="h1"
            description={ <span style={{ fontSize: '18px', color:'white'}}>
            Sign in to view regions and start a Workbench stream.</span>}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="primary" onClick={handleSignIn}>Sign In</Button>
                {authState.isAuthenticated && (<Button variant="primary" onClick={handleSignOut}>Sign Out</Button>)}
              </SpaceBetween>
            }>
            <img 
              src={workbenchLogo} 
              alt="Workbench Streams"
              style={{
                height: '48px',
                width: 'auto',
                display: 'inline-block',
                verticalAlign: 'middle',
                padding: '5px', // Optional: adds some space around the image
                borderRadius: '4px' // Optional: rounds the corners
              }}
            />
          </Header>
          </div>             
        </SpaceBetween>
      }>
    </ContentLayout>
  );
}